import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';
import { LocalStorageService } from '../services/local-storage.service';

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {
	private readonly valleLabUrl: string = environment.urlApiValleLab;

	constructor(
		private readonly ls: LocalStorageService,
		private readonly authService: AuthService,
		private readonly router: Router,
	) {}

	intercept(
		req: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		if (req.url.includes('demosviaalavida')) {
			return this.handleDemosViaAlavidaRequest(req, next);
		}

		if (req.url.includes('@valleLabApi')) {
			return this.handleValleLabApiRequest(req, next);
		}

		if (req.url.includes('auth') && !req.url.includes('logout')) {
			return next.handle(req);
		}

		return this.handleDefaultRequest(req, next);
	}

	private handleDemosViaAlavidaRequest(
		req: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		const tokenSegVial = localStorage.getItem('tokenSegVial');
		if (tokenSegVial) {
			const reqClone = req.clone({
				setHeaders: {
					authorization: `Bearer ${JSON.parse(tokenSegVial)}`,
				},
			});
			return next.handle(reqClone);
		}
		return next.handle(req);
	}

	private handleValleLabApiRequest(
		req: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		const url = req.url.replace('@valleLabApi', this.valleLabUrl);
		const token = this.ls.getItem('tokenValleLab');

		const reqClone = req.clone({
			url: url,
			setHeaders: token ? { authorization: `Bearer ${token}` } : {},
		});

		return next
			.handle(reqClone)
			.pipe(
				catchError((error) => this.handleValleLabAuthError(error, req, next)),
			);
	}

	private handleDefaultRequest(
		req: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		const reqClone = req.clone({
			setHeaders: {
				Authorization: `Bearer ${this.ls.getItem('bearer')}`,
			},
		});

		return next
			.handle(reqClone)
			.pipe(
				catchError((error) => this.handleDefaultAuthError(error, req, next)),
			);
	}

	private handleValleLabAuthError(
		error: HttpErrorResponse,
		originalReq: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		if (error.status === 401) {
			return this.authService.valleLabRefreshToken().pipe(
				catchError(() => {
					this.authService.clearSession();
					window.location.href =
						'/login?redirect=' + encodeURIComponent(this.router.url);
					return throwError(() => error);
				}),
				switchMap(() => {
					return this.retryValleLabRequest(originalReq, next);
				}),
			);
		}

		return throwError(() => error);
	}

	private handleDefaultAuthError(
		error: HttpErrorResponse,
		originalReq: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		if (error.status === 401) {
			return this.authService.refreshToken().pipe(
				catchError(() => {
					this.authService.clearSession();
					window.location.href =
						'/login?redirect=' + encodeURIComponent(this.router.url);
					return throwError(() => error);
				}),
				switchMap(() => {
					return this.retryDefaultRequest(originalReq, next);
				}),
			);
		}

		return throwError(() => error);
	}

	private retryValleLabRequest(
		originalReq: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		const url = originalReq.url.includes('@valleLabApi')
			? originalReq.url.replace('@valleLabApi', this.valleLabUrl)
			: originalReq.url;
		const token = this.ls.getItem('tokenValleLab');

		const retryReq = originalReq.clone({
			url: url,
			setHeaders: token ? { authorization: `Bearer ${token}` } : {},
		});

		return next.handle(retryReq);
	}

	private retryDefaultRequest(
		originalReq: HttpRequest<any>,
		next: HttpHandler,
	): Observable<HttpEvent<any>> {
		const retryReq = originalReq.clone({
			setHeaders: {
				Authorization: `Bearer ${this.ls.getItem('bearer')}`,
			},
		});

		return next.handle(retryReq);
	}
}
